import { Injectable } from '@angular/core';
import {
    BrowserCacheLocation,
    InteractionType,
    LogLevel,
    PublicClientApplication,
} from '@azure/msal-browser';
import { MsalInterceptorConfiguration } from '@azure/msal-angular';
import { environment } from '../environments/environment';
import { AppConfig } from './shared/models/appConfig';

@Injectable()
export class AppConfigService {
    /**
     * Gets the app/environment configs for dependencies. Technically, unnecessary after moving them to the environment
     * files but keeping it required the least amount of refactoring.
     */
    getConfig = (): AppConfig => environment;
}

/**
 * Sets `console.log` as the logger.
 * @param logLevel Unused.
 * @param message The message to log.
 * @param piiEnabled Unused.
 */
export function loggerCallback(
    logLevel: LogLevel,
    message: string,
    piiEnabled: boolean,
) {
    // console.log(message);
}

/**
 * Creates the MSAL configuration from the app configs.
 * @param appConfig The configs parsed from the config.json file.
 */
export function createMsalConfig(
    appConfig: AppConfig,
): PublicClientApplication {
    const isIE =
        window.navigator.userAgent.indexOf('MSIE ') > -1 ||
        window.navigator.userAgent.indexOf('Trident/') > -1;

    return new PublicClientApplication({
        auth: {
            clientId: appConfig.clientId,
            authority:
                'https://login.microsoftonline.com/' + appConfig.tenantId,
            redirectUri: appConfig.redirectUri,
            postLogoutRedirectUri: appConfig.basePath,
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
            // set to true for IE 11. Remove this line to use Angular Universal
            storeAuthStateInCookie: isIE,
        },
        system: {
            loggerOptions: {
                loggerCallback,
                logLevel: LogLevel.Verbose,
                piiLoggingEnabled: appConfig.piiLoggingEnabled,
            },
        },
    });
}

/**
 * Configures MSAL to intercept api calls and acquire an access token to be sent in the Authorization header.
 * @param appConfig The configs parsed from the config.json file.
 */
export function createMsalInterceptor(
    appConfig: AppConfig,
): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();
    // Add protected API endpoints
    protectedResourceMap.set('https://localhost:5011/api/**', [
        appConfig.apiClientScope,
    ]);
    protectedResourceMap.set(appConfig.apiUrl + 'search', [
        appConfig.apiClientScope,
    ]);
    protectedResourceMap.set(appConfig.apiUrl + 'excelvalidations', [
        appConfig.apiClientScope,
    ]);
    protectedResourceMap.set(appConfig.apiUrl + 'submit', [
        appConfig.apiClientScope,
    ]);
    protectedResourceMap.set(`${appConfig.microServiceUrl}**`, [
        appConfig.apiClientScope,
    ]);
    protectedResourceMap.set(`${appConfig.apiUrl}**`, [
        appConfig.apiClientScope,
    ]);

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
    };
}
